import { useState, useCallback, useRef } from "react";
import {
  Image,
  Download,
  Copy,
  Check,
  RefreshCw,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import type { PosterStatus } from "@invitation-system/shared";
import { regeneratePoster } from "../api/client";

interface PosterSectionProps {
  inviterId: string;
  posterUrl?: string;
  posterStatus?: PosterStatus;
  displayName: string;
  onRegenerated?: () => void;
}

type CopyLinkState = "idle" | "copied" | "failed";

/**
 * PosterSection — shown inside GeneratedUrlCard for sponsor/speaker inviters.
 * Renders poster thumbnail, status badge, and action buttons.
 */
export function PosterSection({
  inviterId,
  posterUrl,
  posterStatus,
  displayName,
  onRegenerated,
}: PosterSectionProps) {
  const [copyState, setCopyState] = useState<CopyLinkState>("idle");
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenError, setRegenError] = useState("");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopyLink = useCallback(async () => {
    if (!posterUrl) return;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    try {
      await navigator.clipboard.writeText(posterUrl);
      setCopyState("copied");
      timerRef.current = setTimeout(() => {
        setCopyState("idle");
        timerRef.current = null;
      }, 2000);
    } catch {
      setCopyState("failed");
    }
  }, [posterUrl]);

  const handleRegenerate = useCallback(async () => {
    setRegenError("");
    setIsRegenerating(true);
    try {
      await regeneratePoster(inviterId);
      onRegenerated?.();
    } catch {
      setRegenError("重新生成失败，请稍后重试。");
    } finally {
      setIsRegenerating(false);
    }
  }, [inviterId, onRegenerated]);

  // Ready — show thumbnail + actions
  if (posterStatus === "ready" && posterUrl) {
    return (
      <div className="mt-3 rounded-lg border border-border bg-background p-3">
        <div className="flex items-start gap-3">
          {/* Thumbnail */}
          <a
            href={posterUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 block rounded-md overflow-hidden border border-border hover:border-primary-light transition-colors"
            aria-label={`查看 ${displayName} 的海报大图`}
          >
            <img
              src={posterUrl}
              alt={`${displayName} 海报`}
              className="w-16 h-28 object-cover"
              loading="lazy"
            />
          </a>

          {/* Info + actions */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-2">
              <Image className="h-3.5 w-3.5 text-accent-dark" aria-hidden="true" />
              <span className="text-xs font-medium text-accent-dark">海报已就绪</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {/* Download */}
              <a
                href={posterUrl}
                download
                className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs font-medium text-muted hover:text-foreground hover:border-primary-light transition-colors"
                aria-label="下载海报"
              >
                <Download className="h-3 w-3" aria-hidden="true" />
                下载
              </a>

              {/* Copy link */}
              {copyState === "copied" ? (
                <span className="inline-flex items-center gap-1 rounded-md bg-success-light px-2 py-1 text-xs font-medium text-accent-dark">
                  <Check className="h-3 w-3" aria-hidden="true" />
                  已复制
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs font-medium text-muted hover:text-foreground hover:border-primary-light transition-colors"
                  aria-label="复制海报链接"
                >
                  <Copy className="h-3 w-3" aria-hidden="true" />
                  复制链接
                </button>
              )}

              {/* Regenerate */}
              <button
                type="button"
                onClick={handleRegenerate}
                disabled={isRegenerating}
                className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs font-medium text-muted hover:text-foreground hover:border-primary-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="重新生成海报"
              >
                <RefreshCw
                  className={`h-3 w-3 ${isRegenerating ? "animate-spin" : ""}`}
                  aria-hidden="true"
                />
                重新生成
              </button>
            </div>
            {copyState === "failed" && (
              <p className="mt-1 text-xs text-warning">复制失败，请手动复制链接。</p>
            )}
            {regenError && (
              <p role="alert" className="mt-1 text-xs text-error">{regenError}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Pending / Generating — spinner
  if (posterStatus === "pending" || posterStatus === "generating") {
    return (
      <div className="mt-3 rounded-lg border border-border bg-background p-3">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 text-primary animate-spin" aria-hidden="true" />
          <span className="text-xs font-medium text-muted">
            海报生成中…
          </span>
        </div>
      </div>
    );
  }

  // Failed — error badge + retry
  if (posterStatus === "failed") {
    return (
      <div className="mt-3 rounded-lg border border-error/30 bg-error-light p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-error" aria-hidden="true" />
            <span className="text-xs font-medium text-error">海报生成失败</span>
          </div>
          <button
            type="button"
            onClick={handleRegenerate}
            disabled={isRegenerating}
            className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-error/30 bg-surface px-2 py-1 text-xs font-medium text-error hover:bg-error-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="重试生成海报"
          >
            <RefreshCw
              className={`h-3 w-3 ${isRegenerating ? "animate-spin" : ""}`}
              aria-hidden="true"
            />
            重试
          </button>
        </div>
        {regenError && (
          <p role="alert" className="mt-1 text-xs text-error">{regenError}</p>
        )}
      </div>
    );
  }

  // No poster status (standard type or not yet triggered) — render nothing
  return null;
}
